import { useEffect, useState } from 'react';
import { api, type CuentasYearResponse, type Sede } from '../lib/api.js';
import { fmtInt, fmtPct } from '../lib/format.js';
import { KpiCard } from './KpiCard.js';
import {
  ChartCard,
  MAX_CUENTAS_SERIES,
  OTRAS_LABEL,
  coloresPorCuenta,
} from './charts/ChartCard.js';
import { CuentasMensualChart } from './charts/CuentasMensualChart.js';
import { CuentasRankingChart } from './charts/CuentasRankingChart.js';
import { CuentasSedeMatrix } from './charts/CuentasSedeMatrix.js';

const MES_CORTO = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

/**
 * Panel de CUENTAS CORPORATIVAS: noches que produce cada cuenta de la Central de
 * Reservas, por mes y por sede.
 *
 * Es ANUAL y no por rango, como el panel Diamond: el libro nace en grano de mes
 * y el rango por defecto del dashboard (mes en curso) no contiene ningún mes
 * completo. El año sale del filtro de fechas; las sedes, del filtro de sedes.
 */
export function CuentasPanel({
  anio,
  sedesParam,
  sedeList,
}: {
  anio: number;
  sedesParam?: string;
  sedeList: Sede[];
}) {
  const [data, setData] = useState<CuentasYearResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .cuentasYear({ anio, sedes: sedesParam })
      .then((r) => alive && setData(r))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Error cargando cuentas'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [anio, sedesParam]);

  if (loading) return <p className="text-sm text-slate-400">Cargando cuentas…</p>;
  if (error) return <p className="rounded-lg bg-rose-950/60 px-3 py-2 text-rose-300">{error}</p>;
  if (!data) return null;

  if (data.sinDatos) {
    return (
      <ChartCard title={`Cuentas corporativas · ${anio}`}>
        <p className="text-sm text-slate-500">
          Sin producción de cuentas para {anio}. Se alimenta del libro «RESULTADOS ACCOUNTS» de la
          Central de Reservas, que se sube en Ingesta.
        </p>
      </ChartCard>
    );
  }

  // El color sigue a la cuenta según el ranking del AÑO, no del mes visible.
  const conColor = data.ranking.slice(0, MAX_CUENTAS_SERIES).map((c) => c.cuenta);
  const colores = coloresPorCuenta(conColor);
  const hayOtras = data.ranking.length > MAX_CUENTAS_SERIES;

  const sedesEnDato = [...new Set(data.matriz.flatMap((r) => Object.keys(r.porSede)))].sort(
    (a, b) => {
      const ia = sedeList.findIndex((s) => s.codigo === a);
      const ib = sedeList.findIndex((s) => s.codigo === b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    },
  );

  // El share solo es legible si numerador y denominador cubren lo mismo: cuando
  // al libro mensual de noches le faltan sedes/meses, se dice en vez de callarlo.
  const cobertura =
    data.share.paresTotales === 0 ? null : data.share.paresConBase / data.share.paresTotales;
  const shareHint =
    data.share.share == null
      ? 'Falta el libro mensual de noches: sin base para el %'
      : cobertura != null && cobertura < 0.999
        ? `Base parcial: ${data.share.paresConBase} de ${data.share.paresTotales} pares (sede, mes)`
        : undefined;

  const ytdHint = `YTD a ${MES_CORTO[data.hastaMes - 1]} vs ${anio - 1}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          Cuentas corporativas · {anio}
        </h2>
        <span className="text-xs text-slate-500">
          Noches de habitación producidas por cuenta (Central de Reservas) · {ytdHint}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <KpiCard
          label="Noches de cuentas"
          kpi={data.cards.nochesCuentas}
          format={fmtInt}
          hint={ytdHint}
        />
        <KpiCard
          label="% de noches del hotel"
          kpi={data.cards.shareHotel}
          format={fmtPct}
          hint={shareHint}
        />
        <KpiCard
          label="Concentración top 3"
          kpi={data.cards.top3Share}
          format={fmtPct}
          hint="Cuanto más alto, más depende la cadena de pocas cuentas"
        />
        <KpiCard
          label="Cuentas activas"
          kpi={data.cards.cuentasActivas}
          format={fmtInt}
          hint={
            data.lider
              ? `Líder: ${data.lider.cuenta} (${fmtInt(data.lider.noches)} noches)`
              : undefined
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title={`Noches por cuenta y mes · ${anio}`}>
          <CuentasMensualChart
            meses={data.meses}
            series={conColor}
            colores={colores}
            hayOtras={hayOtras}
          />
          {hayOtras && (
            <p className="mt-2 text-xs text-slate-500">
              Las cuentas fuera del top {MAX_CUENTAS_SERIES} se agrupan en «{OTRAS_LABEL}».
            </p>
          )}
        </ChartCard>

        <ChartCard title={`Ranking de cuentas · ${anio} (noches)`}>
          <CuentasRankingChart data={data.ranking} colores={colores} />
        </ChartCard>
      </div>

      <ChartCard title={`Cuenta × sede · ${anio} (noches)`}>
        <CuentasSedeMatrix rows={data.matriz} sedes={sedesEnDato} />
      </ChartCard>
    </div>
  );
}
